import { Link, useNavigate } from "react-router-dom";
import { MouseEvent, useState } from "react";

import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import { Chip } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import PersonIcon from "@mui/icons-material/Person";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import logo from "logo/GRACE_LOGO_V2_WHITE.png";
import { useAuth } from "components/auth/AuthProvider";
import { useUserServiceSignOut } from "hooks/user/base";

const pages = [
  { title: "Projects", path: "/projects" },
  { title: "Users", path: "/users" },
  { title: "Docs", path: "/docs" },
  { title: "Audit", path: "/audit", adminOnly: true },
];

const OnlineIcon = <FiberManualRecordIcon color="success" fontSize="small" />;

export const Navbar = () => {
  const navigate = useNavigate();
  const { auth, user } = useAuth();

  const signout = useUserServiceSignOut();

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (e: MouseEvent<HTMLElement>) => {
    setAnchorElNav(e.currentTarget);
  };

  const handleOpenUserMenu = (e: MouseEvent<HTMLElement>) => {
    setAnchorElUser(e.currentTarget);
  };

  const handleCloseNavMenu = (path: string) => {
    setAnchorElNav(null);
    navigate(path);
  };

  const handleCloseUserMenu = () => setAnchorElUser(null);

  const settings = [
    { title: "Profile", action: () => navigate("/profile") },
    { title: "Sign out", action: signout.mutate },
  ];

  const isAdmin = user.role === "admin";
  const icon = isAdmin ? <SupervisorAccountIcon /> : <PersonIcon />;

  if (!auth) return <nav></nav>;

  return (
    <AppBar position="static" sx={{ bgcolor: "primary.main" }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Link to="/">
            <img src={logo} width="120" alt="logo" />
          </Link>

          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              color="inherit"
              aria-haspopup="true"
              aria-controls="menu-appbar"
              onClick={handleOpenNavMenu}
            >
              <MenuIcon />
            </IconButton>

            <Menu
              id="menu-appbar"
              open={Boolean(anchorElNav)}
              anchorEl={anchorElNav}
              onClose={() => setAnchorElNav(null)}
              keepMounted
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {pages.map(({ title, path, adminOnly }) => (
                <MenuItem
                  key={title}
                  disabled={adminOnly && !isAdmin}
                  onClick={() => handleCloseNavMenu(path)}
                  sx={{ display: adminOnly && !isAdmin ? "none" : "block" }}
                >
                  {title}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {pages.map(({ title, path, adminOnly }) => (
              <Button
                key={title}
                onClick={() => handleCloseNavMenu(path)}
                sx={{
                  my: 2,
                  color: "white",
                  display: adminOnly && !isAdmin ? "none" : "block",
                }}
              >
                {title}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Requests">
              <IconButton
                color="inherit"
                aria-label="requests"
                onClick={() => navigate("/requests/pending")}
                sx={{ mr: 1 }}
              >
                <ApprovalOutlinedIcon color="inherit" />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                <Chip
                  size="small"
                  icon={OnlineIcon}
                  label={user.email}
                  sx={{ color: "inherit", bgcolor: "transparent" }}
                />
              }
            >
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  sx={{ color: "primary.main", bgcolor: "secondary.main" }}
                >
                  {icon}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              keepMounted
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map(({ title, action }) => (
                <MenuItem
                  key={title}
                  onClick={() => {
                    action();
                    handleCloseUserMenu();
                  }}
                >
                  <Typography color="text.primary" textAlign="center">
                    {title}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
